import React, { useRef, useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '../../redux/hooks';
import { lddStateObject } from '../../redux/reducer';
import { getDataReader } from '../../livedataFuncs';
import { useTrustShieldAuth } from './useTrustShieldAuth';
import { onTrustShieldRefresh } from './initTrustShieldSSO';
import { fetchMatterByAppId } from './trustShieldService';
import { buildTrustShieldUrl } from './trustShieldUtils';
import { PRODUCT_NAME } from '../branding';

/**
 * Override .react-frame parent styles so the iframe fills the container
 * without double scrollbars. Restores original styles on unmount.
 */
function useReactFrameFullHeight(ref: React.RefObject<HTMLDivElement | null>): void {
    useEffect(() => {
        const reactFrame = ref.current?.closest('.react-frame') as HTMLElement | null;
        if (!reactFrame) return;

        const prev = {
            overflow: reactFrame.style.overflow,
            padding: reactFrame.style.padding,
            height: reactFrame.style.height,
        };
        reactFrame.style.overflow = 'hidden';
        reactFrame.style.padding = '0';
        reactFrame.style.height = '100%';

        return () => {
            reactFrame.style.overflow = prev.overflow;
            reactFrame.style.padding = prev.padding;
            reactFrame.style.height = prev.height;
        };
    }, [ref]);
}

const TrustShieldTransactionsPage: React.FC = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [initialMatterId, setInitialMatterId] = useState<string | null>(null);
    // `iframeKey` bumps when ReactShell calls /trusted-token (slow path, via
    // `onTrustShieldRefresh`). Each bump unmounts the current iframe element
    // and mounts a fresh one with the same URL — the new iframe reads the
    // per-sub localStorage partition (now repopulated with fresh tokens)
    // and re-bootstraps cleanly. This is what recovers the embedded SPA
    // when it has shown its login screen because its own RT was dead.
    //
    // No "skip first emission" logic: the listener is subscribed in a
    // useEffect (runs post-render), and a /trusted-token call only happens
    // asynchronously after the auth queryFn starts its slow path. While
    // queryFn is in flight, `authLoading` is true and the iframe is not
    // rendered yet — so the bump just sets state before the first render
    // of the iframe element. The iframe is then mounted ONCE at the
    // bumped key value, no double-load.
    const [iframeKey, setIframeKey] = useState(0);
    useReactFrameFullHeight(containerRef);

    useEffect(() => {
        return onTrustShieldRefresh(() => {
            setIframeLoaded(false);  // re-show spinner during iframe reload
            setIframeKey((k) => k + 1);
        });
    }, []);

    const livedataRoot = useAppSelector(
        (state: lddStateObject) => state.currentNavigation.livedataRootContainer.dataRoot
    );
    const SESSION_INFO = useAppSelector((state: lddStateObject) => state.SessionInfo);

    const dataReader = getDataReader(livedataRoot);
    const fileNumber = dataReader.getFileNumber();
    const appIdRaw = dataReader.getAppId();
    const appId = appIdRaw ? Number(appIdRaw) : NaN;
    const hasAppId = Number.isFinite(appId);

    const { jwt, isLoading: authLoading, error: authError } = useTrustShieldAuth(SESSION_INFO.sid, true);

    const { data: matter, isLoading: matterLoading, error: matterError } = useQuery({
        queryKey: ['trustshield-matter', appId],
        queryFn: async () => {
            if (!jwt) throw new Error('Missing TrustShield JWT');
            return fetchMatterByAppId(jwt, appId);
        },
        enabled: !!jwt && hasAppId,
    });

    // Send postMessage to iframe when user switches files (matter changes)
    useEffect(() => {
        if (!matter) return;

        // First load — record the initial matter and let the iframe src handle it
        if (!initialMatterId) {
            setInitialMatterId(matter.id);
            return;
        }

        // Same matter — no navigation needed
        if (matter.id === initialMatterId) return;

        // Matter changed — tell the iframe to navigate internally
        setIframeLoaded(false);
        setInitialMatterId(matter.id);
        iframeRef.current?.contentWindow?.postMessage(
            { type: 'REALTI_FILE_CHANGED', matterId: matter.id, fileNumber },
            window.location.origin
        );
    }, [matter, initialMatterId, fileNumber]);

    if (authLoading || matterLoading) {
        return (
            <div ref={containerRef} className="text-center p-3">
                <Spinner animation="border" role="status" size="sm" />
                <span className="ms-2">Connecting to {PRODUCT_NAME}...</span>
            </div>
        );
    }

    if (authError) {
        return <div ref={containerRef}><Alert variant="danger">Failed to authenticate with {PRODUCT_NAME}.</Alert></div>;
    }

    if (matterError) {
        return <div ref={containerRef}><Alert variant="danger">Failed to load matter from {PRODUCT_NAME}.</Alert></div>;
    }

    if (!matter) {
        return (
            <div ref={containerRef}>
                <Alert variant="info">
                    No {PRODUCT_NAME} matter found for file {fileNumber}.
                </Alert>
            </div>
        );
    }

    const iframeSrc = buildTrustShieldUrl(
        { embedded: 'true' },
        { sid: SESSION_INFO.sid, sso: false }
    ) + `#/matters/${matter.id}?tab=trust`;

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
            {!iframeLoaded && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'white',
                        zIndex: 1,
                    }}
                >
                    <Spinner animation="border" role="status" />
                    <span className="ms-2" style={{ fontSize: '1.5rem' }}>Loading {PRODUCT_NAME}...</span>
                </div>
            )}
            <iframe
                ref={iframeRef}
                key={iframeKey}
                src={iframeSrc}
                title={`${PRODUCT_NAME} Matter`}
                onLoad={() => setIframeLoaded(true)}
                style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                }}
            />
        </div>
    );
};

export default TrustShieldTransactionsPage;
